import React, { useState, useEffect } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { useSelector } from "react-redux";
import { NumericTextBox } from "@progress/kendo-react-inputs";
import { useAppDispatch } from "../Core/store.hooks";
import {
    TabStrip,
    TabStripTab,
    GridLayout,
    GridLayoutItem,
    Card,
    CardTitle,
    CardImage,
} from "@progress/kendo-react-layout";

type Handler = () => void;

interface ExternalNavParams {
    customerID?: string | number | null;
    mainMachineSerialNo?: string | null;
    customerAssemblyDeviceIDs?: string | null;
}

interface ExternalNavProps {
    visible: boolean;
    toggleVisible: Handler;
    selectedRoute: string;
    params: ExternalNavParams;
}

const ExternalNav = (props: ExternalNavProps) => {
    const [selected, setSelected] = useState(0);
    const [colWidth, setColWidth] = React.useState(180);

    const handleSelect = (e: any) => {
        setSelected(e.selected);
    };

    const openInNewTab = (url: string) => {
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const getFirstCadId = (
        customerAssemblyDeviceIDs?: string | null
    ): string | undefined => {
        if (!customerAssemblyDeviceIDs) {
            return undefined;
        }
        try {
            const parsed = JSON.parse(customerAssemblyDeviceIDs);
            if (Array.isArray(parsed) && parsed.length > 0) {
                return String(parsed[0]);
            }
        } catch {
            // swallow JSON parse error – invalid format
        }
        return undefined;
    };

    const openLink = (linkName: string) => {
        let params = "";

        if (linkName === "search") {
            openInNewTab("https://fleetmgmt.powerplatform.net/search");
        } else if (linkName === "machinedata") {
            const customer = props.params.customerID;

            if (props.params.mainMachineSerialNo != null && customer != null) {
                params =
                    "customerid=" +
                    encodeURIComponent(String(customer)) +
                    "&mainMachineSerialNo=" +
                    encodeURIComponent(props.params.mainMachineSerialNo);
            } else if (customer != null) {
                params = "customerid=" + encodeURIComponent(String(customer));
            }

            if (props.selectedRoute.includes("alarm")) {
                openInNewTab(
                    "https://machinedata.powerplatform.net/alarms/" + params
                );
            } else if (props.selectedRoute.includes("machinetimes")) {
                openInNewTab(
                    "https://machinedata.powerplatform.net/machinetimes/" +
                    params
                );
            } else if (props.selectedRoute.includes("interrupt")) {
                openInNewTab(
                    "https://machinedata.powerplatform.net/triggers/" + params
                );
            } else if (props.selectedRoute.includes("partwearout")) {
                openInNewTab(
                    "https://machinedata.powerplatform.net/mdcTreeView/" +
                    params
                );
            } else if (props.selectedRoute.includes("mdc")) {
                openInNewTab(
                    "https://machinedata.powerplatform.net/timeSeries/" + params
                );
            } else {
                openInNewTab(
                    "https://machinedata.powerplatform.net/events/" + params
                );
            }
        } else if (linkName === "global") {
            // Global links commented out in original – left as-is.
        } else if (linkName === "radar") {
            openInNewTab("https://fleetradar.powerplatform.net/");
        } else if (linkName === "user") {
            openInNewTab("https://usermgmt.powerplatform.net/");
        } else if (linkName === "data") {
            openInNewTab("https://remote-care-monitoring.powerplatform.net/");
        } else if (linkName === "system") {
            const cadId = getFirstCadId(props.params.customerAssemblyDeviceIDs);
            if (cadId) {
                params = "?cadid=" + encodeURIComponent(cadId);
            } else {
                params = "";
            }
            openInNewTab(
                "https://system-monitoring.powerplatform.net/alerts" + params
            );
        } else if (linkName === "service") {
            const customer = props.params.customerID;
            const machine = props.params.mainMachineSerialNo;

            if (machine != null && customer != null) {
                openInNewTab(
                    "https://fleetmgmt.powerplatform.net/customers?customerId=" +
                    encodeURIComponent(String(customer)) +
                    "&machine=" +
                    encodeURIComponent(machine)
                );
            } else if (customer == null) {
                // fixed original typo "feetmgmt"
                openInNewTab("https://fleetmgmt.powerplatform.net/search");
            } else {
                openInNewTab(
                    "https://fleetmgmt.powerplatform.net/customers?customerId=" +
                    encodeURIComponent(String(customer))
                );
            }
        } else if (linkName === "theBox") {
            openInNewTab("https://thebox.powerplatform.net/");
        } else if (linkName === "licenseManagement") {
            openInNewTab("https://licenses.powerplatform.net/");
        } else if (linkName === "download") {
            openInNewTab("https://download.powerplatform.net/");
        } else if (linkName === "fileSharingService") {
            openInNewTab("https://filesharing.powerplatform.net/");
        } else if (linkName === "competenceManagement") {
            openInNewTab("https://competence-management.powerplatform.net/");
        } else if (linkName === "academy") {
            openInNewTab("https://primapower.mrooms.net/");
        } else if (linkName === "cloudSimulator") {
            openInNewTab("https://cloud-simulator.powerplatform.net/");
        } else if (linkName === "jira") {
            openInNewTab("https://primapower-jira.atlassian.net/");
        } else if (linkName === "spareparts") {
            openInNewTab("https://spareparts.primapower.fi/");
        } else if (linkName === "parameterEditor") {
            openInNewTab("https://parameterhelp-editor.powerplatform.net/");
        }
    };

    const renderDialog = () => {
        if (!props.visible) {
            return null;
        }

        return (
            <Dialog
                title={"External Navigation"}
                onClose={props.toggleVisible}
                width={630}
                height={620}
                contentStyle={{ backgroundColor: "#f0f0f0" }}
            >
                <div style={{ backgroundColor: "#f0f0f0", padding: "10px" }}>
                    <div style={{ width: "400px", display: "block" }}>
                        <p>Prima Power cloud applications</p>
                    </div>
                    <GridLayout
                        cols={[
                            {
                                width: 130,
                            },
                            {
                                width: 130,
                            },
                            {
                                width: 130,
                            },
                            {
                                width: 130,
                            },
                        ]}
                        gap={{
                            rows: 15,
                            cols: 15,
                        }}
                    >
                        <GridLayoutItem>
                            <div onClick={() => openLink("theBox")}>
                                <Card
                                    style={{
                                        width: 120,
                                        borderColor: "#a1a1a1",
                                        cursor: "pointer",
                                    }}
                                >
                                    <CardTitle
                                        style={{
                                            fontSize: "13px",
                                            height: "40px",
                                            borderBottom: "1px solid #eee",
                                            padding: "5px",
                                            fontWeight: "bold",
                                            textAlign: "center",
                                            color: "#5e5e5e",
                                        }}
                                    >
                                        The Box
                                    </CardTitle>
                                    <CardImage
                                        src="../../assets/img/TheBox-link.png"
                                        style={{
                                            height: "80px",
                                            width: "130px",
                                            padding: "10px 5px",
                                        }}
                                    />
                                </Card>
                            </div>
                        </GridLayoutItem>

                        <GridLayoutItem>
                            <div onClick={() => openLink("licenseManagement")}>
                                <Card
                                    style={{
                                        width: 120,
                                        borderColor: "#a1a1a1",
                                        cursor: "pointer",
                                    }}
                                >
                                    <CardTitle
                                        style={{
                                            fontSize: "13px",
                                            height: "40px",
                                            borderBottom: "1px solid #eee",
                                            padding: "5px",
                                            fontWeight: "bold",
                                            textAlign: "center",
                                            color: "#5e5e5e",
                                        }}
                                    >
                                        License Management
                                    </CardTitle>
                                    <CardImage
                                        src="../../assets/img/License-Management-link.png"
                                        style={{
                                            height: "80px",
                                            width: "130px",
                                            padding: "10px 5px",
                                        }}
                                    />
                                </Card>
                            </div>
                        </GridLayoutItem>

                        <GridLayoutItem>
                            <div onClick={() => openLink("jira")}>
                                <Card
                                    style={{
                                        width: 120,
                                        borderColor: "#a1a1a1",
                                        cursor: "pointer",
                                    }}
                                >
                                    <CardTitle
                                        style={{
                                            fontSize: "13px",
                                            height: "40px",
                                            borderBottom: "1px solid #eee",
                                            padding: "5px",
                                            fontWeight: "bold",
                                            textAlign: "center",
                                            color: "#5e5e5e",
                                        }}
                                    >
                                        JIRA
                                    </CardTitle>
                                    <CardImage
                                        src="../../assets/img/jira-link.png"
                                        style={{
                                            height: "80px",
                                            width: "120px",
                                            padding: "10px 5px",
                                        }}
                                    />
                                </Card>
                            </div>
                        </GridLayoutItem>

                        <GridLayoutItem>
                            <div onClick={() => openLink("spareparts")}>
                                <Card
                                    style={{
                                        width: 120,
                                        borderColor: "#a1a1a1",
                                        cursor: "pointer",
                                    }}
                                >
                                    <CardTitle
                                        style={{
                                            fontSize: "13px",
                                            height: "40px",
                                            borderBottom: "1px solid #eee",
                                            padding: "5px",
                                            fontWeight: "bold",
                                            textAlign: "center",
                                            color: "#5e5e5e",
                                        }}
                                    >
                                        Spare Parts
                                    </CardTitle>
                                    <CardImage
                                        src="../../assets/img/Spare-parts-link.png"
                                        style={{
                                            height: "80px",
                                            width: "130px",
                                            padding: "10px 5px",
                                        }}
                                    />
                                </Card>
                            </div>
                        </GridLayoutItem>

                        <GridLayoutItem>
                            <div onClick={() => openLink("academy")}>
                                <Card
                                    style={{
                                        width: 120,
                                        borderColor: "#a1a1a1",
                                        cursor: "pointer",
                                    }}
                                >
                                    <CardTitle
                                        style={{
                                            fontSize: "13px",
                                            height: "40px",
                                            borderBottom: "1px solid #eee",
                                            padding: "5px",
                                            fontWeight: "bold",
                                            textAlign: "center",
                                            color: "#5e5e5e",
                                        }}
                                    >
                                        Academy
                                    </CardTitle>
                                    <CardImage
                                        src="../../assets/img/Academy-link.png"
                                        style={{
                                            height: "80px",
                                            width: "130px",
                                            padding: "10px 5px",
                                        }}
                                    />
                                </Card>
                            </div>
                        </GridLayoutItem>

                        <GridLayoutItem>
                            <div onClick={() => openLink("cloudSimulator")}>
                                <Card
                                    style={{
                                        width: 120,
                                        borderColor: "#a1a1a1",
                                        cursor: "pointer",
                                    }}
                                >
                                    <CardTitle
                                        style={{
                                            fontSize: "13px",
                                            height: "40px",
                                            borderBottom: "1px solid #eee",
                                            padding: "5px",
                                            fontWeight: "bold",
                                            textAlign: "center",
                                            color: "#5e5e5e",
                                        }}
                                    >
                                        Cloud Simulator
                                    </CardTitle>
                                    <CardImage
                                        src="../../assets/img/Cloud-Simulator-link.png"
                                        style={{
                                            height: "80px",
                                            width: "130px",
                                            padding: "10px 5px",
                                        }}
                                    />
                                </Card>
                            </div>
                        </GridLayoutItem>

                        <GridLayoutItem>
                            <div onClick={() => openLink("parameterEditor")}>
                                <Card
                                    style={{
                                        width: 120,
                                        borderColor: "#a1a1a1",
                                        cursor: "pointer",
                                    }}
                                >
                                    <CardTitle
                                        style={{
                                            fontSize: "13px",
                                            height: "40px",
                                            borderBottom: "1px solid #eee",
                                            padding: "5px",
                                            fontWeight: "bold",
                                            textAlign: "center",
                                            color: "#5e5e5e",
                                        }}
                                    >
                                        Parameter Editor
                                    </CardTitle>
                                    <CardImage
                                        src="../../assets/img/Parameter-Editor-link.png"
                                        style={{
                                            height: "80px",
                                            width: "130px",
                                            padding: "10px 5px",
                                        }}
                                    />
                                </Card>
                            </div>
                        </GridLayoutItem>

                        <GridLayoutItem>
                            <div onClick={() => openLink("competenceManagement")}>
                                <Card
                                    style={{
                                        width: 120,
                                        borderColor: "#a1a1a1",
                                        cursor: "pointer",
                                    }}
                                >
                                    <CardTitle
                                        style={{
                                            fontSize: "13px",
                                            height: "40px",
                                            borderBottom: "1px solid #eee",
                                            padding: "5px",
                                            fontWeight: "bold",
                                            textAlign: "center",
                                            color: "#5e5e5e",
                                        }}
                                    >
                                        Competence Management
                                    </CardTitle>
                                    <CardImage
                                        src="../../assets/img/Competence-management-link.png"
                                        style={{
                                            height: "80px",
                                            width: "130px",
                                            padding: "10px 5px",
                                        }}
                                    />
                                </Card>
                            </div>
                        </GridLayoutItem>

                        <GridLayoutItem>
                            <div onClick={() => openLink("fileSharingService")}>
                                <Card
                                    style={{
                                        width: 120,
                                        borderColor: "#a1a1a1",
                                        cursor: "pointer",
                                    }}
                                >
                                    <CardTitle
                                        style={{
                                            fontSize: "13px",
                                            height: "40px",
                                            borderBottom: "1px solid #eee",
                                            padding: "5px",
                                            fontWeight: "bold",
                                            textAlign: "center",
                                            color: "#5e5e5e",
                                        }}
                                    >
                                        File Sharing Service
                                    </CardTitle>
                                    <CardImage
                                        src="../../assets/img/File-Sharing-Service-link.png"
                                        style={{
                                            height: "80px",
                                            width: "130px",
                                            padding: "10px 5px",
                                        }}
                                    />
                                </Card>
                            </div>
                        </GridLayoutItem>

                        <GridLayoutItem>
                            <div onClick={() => openLink("download")}>
                                <Card
                                    style={{
                                        width: 120,
                                        borderColor: "#a1a1a1",
                                        cursor: "pointer",
                                    }}
                                >
                                    <CardTitle
                                        style={{
                                            fontSize: "13px",
                                            height: "40px",
                                            borderBottom: "1px solid #eee",
                                            padding: "5px",
                                            fontWeight: "bold",
                                            textAlign: "center",
                                            color: "#5e5e5e",
                                        }}
                                    >
                                        Software Download
                                    </CardTitle>
                                    <CardImage
                                        src="../../assets/img/Software-Download-link.png"
                                        style={{
                                            height: "80px",
                                            width: "130px",
                                            padding: "10px 5px",
                                        }}
                                    />
                                </Card>
                            </div>
                        </GridLayoutItem>

                        <GridLayoutItem>
                            <div onClick={() => openLink("user")}>
                                <Card
                                    style={{
                                        width: 120,
                                        borderColor: "#a1a1a1",
                                        cursor: "pointer",
                                    }}
                                >
                                    <CardTitle
                                        style={{
                                            fontSize: "14px",
                                            height: "40px",
                                            borderBottom: "1px solid #eee",
                                            padding: "5px",
                                            fontWeight: "bold",
                                            textAlign: "center",
                                            color: "#5e5e5e",
                                        }}
                                    >
                                        User Management
                                    </CardTitle>
                                    <CardImage
                                        src="../../assets/img/user-mgmt-link.png"
                                        style={{
                                            height: "80px",
                                            width: "130px",
                                            padding: "10px 5px",
                                        }}
                                    />
                                </Card>
                            </div>
                        </GridLayoutItem>

                        <GridLayoutItem>
                            <div onClick={() => openLink("system")}>
                                <Card
                                    style={{
                                        width: 120,
                                        borderColor: "#a1a1a1",
                                        cursor: "pointer",
                                    }}
                                >
                                    <CardTitle
                                        style={{
                                            fontSize: "14px",
                                            height: "40px",
                                            borderBottom: "1px solid #eee",
                                            padding: "5px",
                                            fontWeight: "bold",
                                            textAlign: "center",
                                            color: "#5e5e5e",
                                        }}
                                    >
                                        System Monitoring
                                    </CardTitle>
                                    <CardImage
                                        src="../../assets/img/system-mon-link.png"
                                        style={{
                                            height: "80px",
                                            width: "130px",
                                            padding: "10px 5px",
                                        }}
                                    />
                                </Card>
                            </div>
                        </GridLayoutItem>
                    </GridLayout>
                </div>
                <DialogActionsBar>
                    <button
                        className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
                        onClick={props.toggleVisible}
                    >
                        Close
                    </button>
                </DialogActionsBar>
            </Dialog>
        );
    };

    return renderDialog();
};

export default ExternalNav;
